<template>
  <div class="artist-list">
    <div
      class="artist-list-header"
      :style="{borderTop: `2px solid ${level.color}`}"
    >
      <b-tooltip
        :label="level.tooltip"
        dashed
        multilined
        type="is-dark"
      >
        <span>{{ level.label }}</span>
      </b-tooltip>
      <div class="icons">
        <b-tooltip
          type="is-dark"
          label="Filtrer par auteur"
        >
          <div
            class="icon"
            :class="{'is-active': filter == 'author'}"
            @click="changeFilter('author')"
          >
            <i class="mdi mdi-pencil" />
          </div>
        </b-tooltip>
        <b-tooltip
          type="is-dark"
          label="Filtrer par compositeur"
        >
          <div
            class="icon"
            :class="{'is-active': filter == 'composer'}"
            @click="changeFilter('composer')"
          >
            <i class="mdi mdi-music" />
          </div>
        </b-tooltip>
        <b-tooltip
          type="is-dark"
          label="Filtrer par interprète"
        >
          <div
            class="icon"
            :class="{'is-active': filter == 'interpret'}"
            @click="changeFilter('interpret')"
          >
            <i class="mdi mdi-microphone" />
          </div>
        </b-tooltip>
      </div>
    </div>
    <div class="artist-list-wrapper">
      <div class="artist-list-content">
        <div
          v-for="(item, i) in filteredData"
          :key="item.id"
          class="artist-list-item"
          :class="classes(item.artist.id, i)"
          @click="toArtist(item.artist)"
          @mouseover="$emit('mouseoverartist', {id: item.artist.id, level: levelId})"
          @mouseleave="$emit('mouseoverartist', {id:null, level:null})"
        >
          <div class="artist-list-item-body">
            <div class="data">
              <div class="name">
                {{ item.artist.name }}
              </div>
              <div class="info">
                {{ item.scores[levelId].total }} implications
              </div>
            </div>
            <div class="graphs">
              <b-tooltip
                v-for="role in ['author', 'composer', 'interpret']"
                :key="role"
                multilined
                class="graph"
                :class="`graph-${role}`"
                :label="labelText(role, item, levelId)"
                type="is-dark"
                size="is-small"
                position="is-left"
              >
                <div
                  class="graph-line"
                  :style="{height: (item.scores[levelId][role]/item.scores[levelId].total)*(item.scores[levelId].total/max)*100 + '%'}"
                  @mouseover="$emit('mouseoverrole', role)"
                  @mouseleave="$emit('mouseoverrole', null)"
                />
              </b-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { toArtist } from "../../services/navigator";

export default {
	props: {
		levelId: {
			type: Number,
			required: true
		},
		data: {
			type: Array,
			required: true
		},
		level: {
			type: Object,
			required: true
		},
		artistId: {
			type: String,
			required: true
		},
		canNavigate: {
			type: Boolean,
			default: true
		},
	},
	data () {
		return {
			filter: null
		};
	},  
	computed: {
		max () {
			return this.data[0].scores[this.levelId].total;
		},
		filteredData () {
			return this.data.filter(item => {
				if (this.filter == null) {
					return true;
				}
				return item.scores[this.levelId][this.filter] > 0;
			}).sort((a,b) => b.scores[this.levelId][this.filter] - a.scores[this.levelId][this.filter]);
		}
	},
	methods: {
		roleName (role) {
			if (role == "author") {
				return "auteur";
			}
			if (role == "interpret") {
				return "interprète";
			}
			if (role == "composer") {
				return "compositeur";
			}
			return "";
		},
		labelText (role, item, levelId) {
			let n = item.scores[levelId][role];
			if (n > 1) {
				return `Dans ${n} chansons, ${item.artist.name} a été ${this.roleName(role)}`;
			}
			if (n == 1) {
				return `Pour ${n} chanson, ${item.artist.name} a été ${this.roleName(role)}`;
			}
			if (n == 0) {
				return `${item.artist.name} n'a jamais été ${this.roleName(role)}`;
			}
			return "";
		},
		classes (id, i) {
			let classes = [`item-${i}`];
			if (id == this.artistId) {
				classes.push("is-main");
			}
			if (!this.canNavigate) {
				classes.push("no-nav");
			}
			return classes.join(" ");
		},
		toArtist (artist) {
			if (this.canNavigate) {
				toArtist(artist.id);
			}
		},
		changeFilter (filter) {        
			if (this.filter == filter) {
				this.filter = null;
			} else {
				this.filter = filter;
			}
		}
	}
};
</script>

<style lang="scss" scoped>

$headerHeight: 35px;
$itemHeight: 45px;
$graphHeight: 30px;
$graphSpacing: 1px;

.artist-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #f1f1f1;
}

.artist-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: $headerHeight;
    box-shadow: 1px 1px 3px rgba(0,0,0,0.1);
    z-index: 2;
    position: relative;
    padding-left: 0.7rem;
    padding-right: 20px;
    font-size: 0.9rem;
    font-weight: 600;

    .icon {
        width: 1.2rem;
        cursor: pointer;
        color: #999999;

        &:hover {
            color: #333;
        }

        &.is-active {
            color: #000;

            &:hover {
                color: #000;
            }
        }
    }
}

.artist-list-content {
    height: $itemHeight * 8 - 1;
    overflow-y: scroll;
}

.artist-list-item {
    height: $itemHeight;
    border-bottom: 1px solid #f4f4f4;
    cursor: pointer;

    .artist-list-item-body {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 100%;
      padding-left: 0.7rem;
      padding-right: 0.2rem;
      line-height: 1;
    }

    &.is-main {
      background-color: #fcfcfc;
      cursor: auto;
      .name {
        font-weight: 700;
      }
    }

    &.no-nav {
      cursor: default;
    }

    .data {
        display: block;
    }

    .name {
        color: #333;
        font-size: 0.87rem;
        font-weight: 500;
        margin-bottom: 2px;
    }

    .info {
        color: #777;
        font-weight: 400;
        font-size: 0.7rem;
    }

    &:hover {
        background-color: #f9f9f9;
    }

    .graphs {
        display: flex;
        height: 100%;
        .graph {
            opacity: 1;
            margin-left: $graphSpacing*3;
            padding-top: $graphSpacing*3;
            height: 100%;
            display: flex;
            align-items: flex-end;
            background-color: #f5f5f5;

            &:hover {
              &.graph-author .graph-line { background-color: #3182ce; }
              &.graph-composer .graph-line { background-color: #805ad5; }
              &.graph-interpret .graph-line { background-color: #319795; }
            }

            .graph-line {
                height: ($graphHeight)/10 - $graphSpacing;
                background-color: #e3e3e3;
                width: 20px;
            }

            &.graph-author .graph-line { background-color: #4299e1; }
            &.graph-composer .graph-line { background-color: #9f7aea; }
            &.graph-interpret .graph-line { background-color: #38b2ac; }
        }
    }
}
</style>
